import { sortBy, uniqBy } from 'es-toolkit'
import moment from 'moment'
import { useState, useMemo, Fragment } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Popup from 'reactjs-popup'
import { Card, CardBody } from 'reactstrap'

import { COLOR_TYPES } from 'api/constants'
import type {
  WorkspaceSummaryData,
  WorkspaceSummaryWorker,
  WorkspaceSummaryHourlyWorkerData,
} from 'api/dashboard/types'

import { selectPlansStatus } from 'slices/plansSlice'
import { selectTenantsStatus } from 'slices/tenantsSlice'

import { BadgeButton } from 'components/common'
import { TENTATIVE_SCHEDULE_TYPE_ID } from 'components/common/constants'
import type { BadgeItem } from 'components/common/types'

import useBusinessTime from 'hooks/useBusinessTime'
import useDateQuery from 'hooks/useDateQuery'
import { useGroupPerformance } from 'hooks/useGroupPerformance'
import usePlans from 'hooks/usePlans'

import { NULL_GROUP_ID } from './utils'

import styles from './PerformanceList.module.scss'

export type Props = {
  workspaceId: number
  workspaceSummaryData: WorkspaceSummaryData | undefined
}

const PerformanceList = (props: Props) => {
  const { workspaceId, workspaceSummaryData } = props
  const [openGroupIds, setOpenGroupIds] = useState<Array<number | string>>([])
  const navigate = useNavigate()
  const { plans } = useSelector(selectPlansStatus, shallowEqual)
  const { tenantWithDate } = useSelector(selectTenantsStatus, shallowEqual)

  const { businessHourBlocks } = useBusinessTime({ tenantWithDate })
  const { getEditSchedulesFromWorkPlan } = usePlans()
  const date = useDateQuery()
  const { getGroupPerformance } = useGroupPerformance()

  const groupPerformances = useMemo(() => {
    if (!workspaceSummaryData) {
      return []
    }
    return getGroupPerformance(workspaceSummaryData.groups)
  }, [workspaceSummaryData, getGroupPerformance])

  const onGroupToggleClick = (groupId: number | string) => {
    const newOpenGroupIds = openGroupIds.includes(groupId)
      ? openGroupIds.filter(id => id !== groupId)
      : openGroupIds.concat([groupId])
    setOpenGroupIds(newOpenGroupIds)
  }
  const isOpen = (groupId: number | string) => openGroupIds.includes(groupId)

  // 現在作業中のスケジュールを抽出する
  const currentWorkerSchedules = useMemo(() => {
    if (!plans || plans.workDate !== moment().format('YYYY-MM-DD')) {
      return []
    }
    return plans.groups.flatMap(group => {
      return group.workersPlan.map(worker => {
        const workerSchedule = getEditSchedulesFromWorkPlan(worker, false)
        const currentSchedule = workerSchedule
          .filter(schedule => schedule.scheduleTypeId !== TENTATIVE_SCHEDULE_TYPE_ID.SHIFT) // シフトを除外する
          .find(schedule => {
            const startAt = moment(schedule.startAt)
            const endAt = startAt.clone().add(schedule.duration, 'second')
            return moment().isBetween(startAt, endAt, null, '[]')
          })
        return {
          workerId: worker.workerId,
          schedule: currentSchedule,
        }
      })
    })
  }, [plans, getEditSchedulesFromWorkPlan])

  const workerBadgeItems = useMemo(() => {
    if (!workspaceSummaryData) {
      return []
    }
    return workspaceSummaryData.groups.flatMap(group => {
      return group.workers.map(worker => {
        const currentSchedule = currentWorkerSchedules.find(current => current.workerId === worker.workerId)?.schedule
        const badgeItems: BadgeItem[] = []
        if (currentSchedule) {
          if (currentSchedule.scheduleTypeId === TENTATIVE_SCHEDULE_TYPE_ID.SUPPORT) {
            // 応援作業の場合
            badgeItems.push({
              color: COLOR_TYPES.SILVER,
              key: -(currentSchedule.supportWorkspaceId || 0), // keyに指定するscheduleTypeIdとsupportWorkspaceIdが競合するためsupportWorkspaceIdをマイナスにする
              label: currentSchedule.supportWorkspaceName || '',
              disabled: true,
            })
          } else {
            const scheduleType = worker.hourlyWorkerData.find(
              data => data.scheduleTypeId === currentSchedule.scheduleTypeId
            )
            if (scheduleType) {
              badgeItems.push({
                color: scheduleType.scheduleTypeColor,
                key: scheduleType.scheduleTypeId,
                label: scheduleType.scheduleTypeName,
                disabled: true,
              })
            }
          }
        }
        return {
          workerId: worker.workerId,
          badgeItems,
        }
      })
    })
  }, [workspaceSummaryData, currentWorkerSchedules])

  const groupBadgeItems = useMemo(() => {
    if (!workspaceSummaryData) {
      return []
    }
    return workspaceSummaryData.groups.map(group => {
      const badgeItems = group.workers.flatMap(worker => {
        return workerBadgeItems.find(workerBadgeItem => workerBadgeItem.workerId === worker.workerId)?.badgeItems || []
      })
      return {
        groupId: group.groupId ?? NULL_GROUP_ID,
        badgeItems: uniqBy(badgeItems, item => item.key),
      }
    })
  }, [workspaceSummaryData, workerBadgeItems])

  const scheduleTypesPopup = (badgeItems: BadgeItem[] | undefined) => {
    return (
      <Popup trigger={<i className={`icf-info text-secondary ${styles.scheduleTypesOpener}`} role="button" />}>
        <Card className="shadow">
          <CardBody className={`p-2 pe-3 ${styles.popupCardBody}`}>
            <div className="p-2">現在実施している作業</div>
            {badgeItems && <BadgeButton items={badgeItems} />}
          </CardBody>
        </Card>
      </Popup>
    )
  }

  const lastImportedAt = useMemo(() => {
    if (!workspaceSummaryData) {
      return
    }
    const flatData = workspaceSummaryData.groups
      .flatMap<WorkspaceSummaryWorker>(group => group.workers)
      .flatMap<WorkspaceSummaryHourlyWorkerData>(worker => worker.hourlyWorkerData)
      .filter(data => data.lastImportedAt)
    const latestWorkspaceSummaryData = sortBy(flatData, ['lastImportedAt']).slice(-1)[0]
    return latestWorkspaceSummaryData
      ? moment(latestWorkspaceSummaryData.lastImportedAt).format('YYYY/MM/DD HH:mm:ss')
      : undefined
  }, [workspaceSummaryData])

  return (
    <Card className="mt-3">
      <CardBody className="fw-bold">グループ/メンバー別パフォーマンスリスト</CardBody>
      <CardBody className="pt-0">
        <div className="d-flex">
          <div className={styles.labelWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={`${styles.teamHeader} bg-secondary-pale p-2`}>グループ</th>
                </tr>
              </thead>

              <tbody>
                {groupPerformances.map(group => (
                  <Fragment key={group.groupId}>
                    <tr key={`tr-${group.groupId}`}>
                      <td className={`${styles.label} d-flex justify-content-between align-items-center p-2`}>
                        <div
                          className={`${styles.workerName} d-flex align-items-center`}
                          role="button"
                          onClick={() => onGroupToggleClick(group.groupId)}
                        >
                          <i className={`icf-carot_${isOpen(group.groupId) ? 'down' : 'right'} me-1`} />
                          <div className="text-truncate">{group.groupName}</div>
                        </div>

                        {scheduleTypesPopup(groupBadgeItems.find(items => items.groupId === group.groupId)?.badgeItems)}
                      </td>
                    </tr>

                    {isOpen(group.groupId) &&
                      group.workerDataList.map(worker => (
                        <tr key={`tr-${group.groupId}-${worker.workerId}`}>
                          <td className={`${styles.label} d-flex justify-content-between align-items-center`}>
                            <div
                              className="text-truncate"
                              role="button"
                              onClick={() =>
                                navigate(
                                  `/dashboard/${workspaceId}/performance-graph/workers/${worker.workerId}?date=${date}`
                                )
                              }
                            >
                              {worker.workerName}
                            </div>

                            {scheduleTypesPopup(
                              workerBadgeItems.find(items => items.workerId === worker.workerId)?.badgeItems
                            )}
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.dataWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {businessHourBlocks.map(h => (
                    <th key={`d-th-${h}`} className={`${styles.dataCell} bg-secondary-pale p-2 text-center`}>
                      {h}:00
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {groupPerformances.map(group => (
                  <Fragment key={`d-${group.groupId}`}>
                    <tr key={`d-tr-${group.groupId}`}>
                      {businessHourBlocks.map(h => {
                        const rate = group.groupData[`${h}:00`]?.rate || null
                        const warning = rate !== null && Math.abs(100 - rate) >= 10
                        return (
                          <td
                            key={`d-td-${group.groupId}-${h}`}
                            className={`${warning ? 'text-danger' : ''} ${styles.dataCell} p-2 text-center`}
                          >
                            {rate ? `${rate}%` : ''}
                          </td>
                        )
                      })}
                    </tr>

                    {isOpen(group.groupId) &&
                      group.workerDataList.map(worker => (
                        <tr key={`d-tr-${group.groupId}-${worker.workerId}`}>
                          {businessHourBlocks.map(h => {
                            const rate = worker.workerData[`${h}:00`]?.rate || null
                            const warning = rate !== null && Math.abs(100 - rate) >= 10
                            return (
                              <td
                                key={`d-td-${group.groupId}-${worker.workerId}-${h}`}
                                className={`${warning ? 'text-danger' : ''} ${styles.dataCell} p-2 text-center`}
                              >
                                {rate ? `${rate}%` : ''}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardBody>

      <CardBody className="d-flex text-muted align-self-end pt-0">
        {lastImportedAt && <i className="icf-updated align-self-center pe-1" />}
        <small className={styles.date}>{lastImportedAt}</small>
      </CardBody>
    </Card>
  )
}

export default PerformanceList
